import { useEffect, useState } from "react";
import { useGetEvents } from "@workspace/api-client-react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal, openNewYearBooking } from "@/components/booking-modal";
import { format, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import { Link } from "wouter";
import { isNewYearEvent, NY_PATH, NY_POSTER, NY_START_ISO } from "@/components/new-year/campaign";

type TabId = "upcoming" | "promo" | "past";

interface EventWithType {
  id: number;
  title: string;
  description: string;
  date: string;
  imageUrl: string;
  isUpcoming: boolean;
  type?: string;
}

export default function Afisha() {
  const [tab, setTab] = useState<TabId>("upcoming");
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Афиша — Паруса на крыше | Мероприятия и акции в Петербурге",
    description: "Афиша событий ресторана Паруса на крыше: живая музыка, джаз-вечера, тематические ужины, праздники и акции. Актуальное расписание на крыше в Санкт-Петербурге.",
    keywords: "афиша ресторан СПб, живая музыка Петербург ресторан, джаз вечер ресторан СПб, мероприятия крыша Петербург, события ресторан Паруса",
    image: "/photos/view_sunset.jpg",
    canonical: "/afisha",
  });
  useBreadcrumbs([{ name: "Афиша", path: "/afisha" }]);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
  }, []);

  const { data: rawEvents } = useGetEvents({ filter: "all" });
  const events = (rawEvents as EventWithType[] | undefined) ?? [];

  useEffect(() => {
    const upcomingEvents = events.filter(
      (e) => e.isUpcoming && (e.type || "event") === "event"
    );
    if (!upcomingEvents.length) return;

    const schemas = upcomingEvents.map((e) => ({
      "@context": "https://schema.org",
      "@type": "Event",
      "name": e.title,
      "description": e.description,
      "startDate": isNewYearEvent(e) ? NY_START_ISO : e.date,
      "url": isNewYearEvent(e) ? `https://parusanakryshe.ru${NY_PATH}` : "https://parusanakryshe.ru/afisha",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "image": isNewYearEvent(e) ? `https://parusanakryshe.ru${NY_POSTER}` : e.imageUrl
        ? (e.imageUrl.startsWith("http") ? e.imageUrl : `https://parusanakryshe.ru${e.imageUrl}`)
        : "https://parusanakryshe.ru/photos/view_sunset.jpg",
      "location": {
        "@type": "Place",
        "name": "Паруса на крыше",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "ул. Марата, 5, 6 этаж",
          "addressLocality": "Санкт-Петербург",
          "postalCode": "191002",
          "addressCountry": "RU",
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 59.930232,
          "longitude": 30.354353,
        },
      },
      "organizer": {
        "@type": "Organization",
        "name": "Паруса на крыше",
        "url": "https://parusanakryshe.ru",
      },
      "performer": {
        "@type": "PerformingGroup",
        "name": e.title,
      },
    }));

    const existing = document.querySelectorAll("script[data-event-schema]");
    existing.forEach((el) => el.remove());

    schemas.forEach((schema) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-event-schema", "true");
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => {
      document.querySelectorAll("script[data-event-schema]").forEach((el) => el.remove());
    };
  }, [events]);

  const formatDate = (dateString: string) => {
    try { return format(parseISO(dateString), "d MMMM, HH:mm", { locale: ru }); }
    catch { return dateString.replace("T", " "); }
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: "upcoming", label: "Мероприятия" },
    { id: "promo", label: "Акции" },
    { id: "past", label: "Архив" },
  ];

  const filtered = events.filter((e) => {
    if (tab === "upcoming") return e.isUpcoming && (e.type || "event") === "event";
    if (tab === "promo") return (e.type || "event") === "promo";
    if (tab === "past") return !e.isUpcoming;
    return true;
  });

  return (
    <div className="w-full min-h-screen bg-background relative z-10 pt-[100px] md:pt-[120px] pb-32 border-t border-primary/20 overflow-x-hidden">
      <div className="container mx-auto px-6 md:px-14 max-w-6xl relative">
        <div className="hidden sm:block absolute top-0 right-0 font-serif text-[130px] md:text-[240px] font-light leading-none text-primary/5 select-none pointer-events-none tracking-tighter -mt-20">Сцена</div>

        <div className="mb-14 md:mb-20 relative z-10" style={{
          opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.5s ease, transform 0.5s ease"
        }}>
          <h1 className="text-4xl md:text-7xl font-serif font-light text-primary mb-6 tracking-wide">Афиша</h1>
          <div className="w-24 h-[1px] bg-primary/30" />
        </div>

        <div className="flex flex-wrap gap-6 md:gap-8 mb-14 md:mb-20 border-b border-primary/20 pb-6 relative z-10" style={{
          opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s"
        }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-[10px] tracking-[0.3em] uppercase pb-2 transition-colors relative ${
                tab === t.id ? "text-accent" : "text-muted-foreground hover:text-primary"
              }`}
            >
              {t.label}
              {tab === t.id && (
                <div className="absolute -bottom-6 left-0 right-0 h-[1px] bg-accent" />
              )}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 border-t border-l border-primary/10 relative z-10" style={{
          opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.5s ease 0.2s, transform 0.5s ease 0.2s"
        }}>
          {filtered.map((event) => {
            const t = event.title.toLowerCase();
            const isPromo = (event.type || "event") === "promo";

            // Pick icon + label by title keywords
            let icon = "🎵";
            let badgeLabel = "Живая музыка";
            let badgeColor = "bg-primary/15 text-primary border-primary/25";

            if (t.includes("скрипка") || t.includes("шишкин") || t.includes("фалин")) {
              icon = "🎻"; badgeLabel = "Скрипка";
            } else if (t.includes("виолончель") || t.includes("пиколин")) {
              icon = "🎻"; badgeLabel = "Виолончель";
            } else if (t.includes("арфа") || t.includes("виктория")) {
              icon = "🎼"; badgeLabel = "Арфа";
            } else if (t.includes("дудук") || t.includes("погосян")) {
              icon = "🎷"; badgeLabel = "Дудук";
            } else if (t.includes("аккордеон")) {
              icon = "🪗"; badgeLabel = "Аккордеон";
            } else if (t.includes("фортепьяно") || t.includes("рояль")) {
              icon = "🎹"; badgeLabel = "Фортепьяно";
            } else if (t.includes("саксофон")) {
              icon = "🎷"; badgeLabel = "Саксофон";
            } else if (t.includes("вокал") || t.includes("марун") || t.includes("фелиция") || t.includes("гвоздняк")) {
              icon = "🎤"; badgeLabel = "Вокал";
            } else if (t.includes("нон стоп") || t.includes("нон-стоп") || t.includes("кайфлото") || t.includes("бинго")) {
              icon = "🎉"; badgeLabel = "Вечеринка"; badgeColor = "bg-accent/10 text-accent border-accent/25";
            } else if (t.includes("семейный") || t.includes("детск")) {
              icon = "👨‍👩‍👧"; badgeLabel = "Семейный день"; badgeColor = "bg-white/5 text-white/60 border-white/15";
            } else if (t.includes("девушек") || t.includes("секретный")) {
              icon = "🌸"; badgeLabel = "День девушек"; badgeColor = "bg-white/5 text-white/60 border-white/15";
            } else if (t.includes("италии") || t.includes("тоскан")) {
              icon = "🍝"; badgeLabel = "День Италии"; badgeColor = "bg-white/5 text-white/60 border-white/15";
            } else if (t.includes("грузин")) {
              icon = "🥩"; badgeLabel = "Грузинский день"; badgeColor = "bg-white/5 text-white/60 border-white/15";
            } else if (t.includes("сладк") || t.includes("вторник")) {
              icon = "🍰"; badgeLabel = "Сладкий вторник"; badgeColor = "bg-white/5 text-white/60 border-white/15";
            } else if (isPromo) {
              icon = "✨"; badgeLabel = "Акция"; badgeColor = "bg-white/5 text-white/60 border-white/15";
            }

            const isNY = isNewYearEvent(event);
            if (isNY) { icon = ""; badgeLabel = "Новогодняя ночь"; badgeColor = "bg-[hsl(var(--ny-gold)/0.12)] text-[hsl(var(--ny-gold))] border-[hsl(var(--ny-gold)/0.4)]"; }

            return (
              <div key={event.id} className={`border-b ${isNY ? "ny-stage" : ""} border-r border-primary/10 p-6 md:p-10 lg:p-12 flex flex-col md:flex-row gap-6 md:gap-16 group ${!event.isUpcoming ? "opacity-40 grayscale" : ""}`}>
                <div className="md:w-1/4 shrink-0 border-l-2 border-primary/40 pl-5">
                  <div className="text-[10px] tracking-[0.2em] text-accent uppercase mb-2">
                    {isPromo ? "Акция" : "Дата"}
                  </div>
                  <div className="font-serif text-xl md:text-2xl font-light text-foreground mb-4">
                    {isPromo
                      ? (event.isUpcoming ? "Сейчас" : "Завершена")
                      : formatDate(event.date)}
                  </div>
                  {/* Icon badge */}
                  <div className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] tracking-[0.1em] ${badgeColor}`}>
                    {icon && <span>{icon}</span>}
                    <span>{badgeLabel}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-2xl md:text-3xl mb-4 font-light text-foreground group-hover:text-primary transition-colors">{event.title}</h3>
                  <p className="text-sm font-light text-muted-foreground leading-relaxed mb-8 max-w-2xl">{event.description}</p>
                  {event.isUpcoming && (
                    <button
                      onClick={() => isNY ? openNewYearBooking("afisha_event") : openBookingModal()}
                      className="bg-transparent border border-primary/45 text-accent px-8 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-primary hover:text-primary-foreground transition-all"
                    >
                      Забронировать
                    </button>
                  )}
                  {isNY && (
                    <Link href={NY_PATH} data-testid="link-afisha-ny" className="inline-block ml-0 sm:ml-3 mt-3 sm:mt-0 border border-[hsl(var(--ny-gold)/0.5)] text-[hsl(var(--ny-gold))] px-8 py-3 text-[9px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--ny-gold))] hover:text-background transition-all">
                      Программа и меню
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="p-20 text-center text-muted-foreground/50 font-light text-sm uppercase tracking-[0.2em] border-b border-r border-primary/10">
              {tab === "promo" ? "Акций пока нет" : tab === "past" ? "Нет прошедших событий" : "Нет предстоящих мероприятий"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
